import React from "react"
import PropTypes from "prop-types"
import { Column } from "react-virtualized"
import moment from "moment"
import bs from "binary-search"
import DataTable, { tooltipHeaderRenderer } from "@swipesense/data-table"
import { AssetSummary } from "@swipesense/asset-components"
import { entitiesPropTypes } from "../../utils/propTypes"
import { exposureSearcher } from "../../utils/search"
import { basicSort } from "../../utils/sort"
import PageBanner from "../PageBanner"
import Loading from "../Loading"
import "./ExposedAssetVisitList.scss"
require("moment-duration-format") // not called directly, but extends moment to add moment.duration()

const ExposedAssetVisitList = (props) => {
  const isExposureEvent = (visit) => {
    const matchingLocations = props.entities.filter(
      (entity) => entity.id === visit.locationId
    )
    return matchingLocations.length > 0
      ? bs(matchingLocations, visit, exposureSearcher) >= 0
      : false
  }

  const sortedVisits = !props.loading
    ? props.visits
        .map((visit) => ({
          ...visit,
          entryAt: moment.parseZone(visit.entryAt),
        }))
        .sort(basicSort("startAt", "DESC"))
    : []

  const nameCellRenderer = (props) => {
    return (
      <div className="nameCell">
        <div className="name">{props.cellData}</div>
        <div className="subName">{props.rowData.unit}</div>
      </div>
    )
  }

  nameCellRenderer.propTypes = {
    cellData: PropTypes.string,
    rowData: PropTypes.object,
  }

  const visitCellRenderer = (props) => {
    const visitDate = moment.parseZone(props.rowData.entryAt)
    const duration = moment.duration(props.rowData.duration, "seconds")
    return (
      <div className="visit">
        <div className="date">{visitDate.format("MMM D")}</div>
        <div className="dot-divider">•</div>
        <div className="time">{visitDate.format("h:mm a").slice(0, -1)}</div>
        <div className="duration">({duration.humanize()})</div>
      </div>
    )
  }

  visitCellRenderer.propTypes = {
    rowData: PropTypes.object,
  }

  const exposureCellRenderer = (props) => {
    return isExposureEvent(props.rowData) ? (
      <div className="exposure-cell">
        <div className="exposure"> !</div>Exposure{" "}
      </div>
    ) : (
      ""
    )
  }

  exposureCellRenderer.propTypes = {
    rowData: PropTypes.object,
  }

  const rowHighlighter = (row) => {
    if (row.index === -1 || !isExposureEvent(sortedVisits[row.index])) {
      return ""
    } else {
      return "exposed-row"
    }
  }

  return (
    <div className="ExposedAssetVisitList">
      <PageBanner className={"exposed-asset-visit-list-banner"}>
        <div className="exposed-asset-visit-header">
          <div className="asset-name">
            <h1>{props.asset.name}</h1>
          </div>
          <AssetSummary
            {...{ ...props.asset, name: "" }}
            deviceType="desktop"
          />
        </div>
      </PageBanner>
      <div className="visits-wrapper">
        <DataTable
          className="exposed-asset-visit-table"
          rowHeight={46}
          data={sortedVisits}
          loading={props.loading}
          loadingComponent={<Loading />}
          rowClassName={rowHighlighter}
          noRowsMessage={"No visits found."}
        >
          <Column
            key="name"
            dataKey="name"
            width={25}
            label="Using Unit"
            cellRenderer={nameCellRenderer}
            disableSort={true}
          />
          <Column
            key="entryAt"
            dataKey="entryAt"
            width={45}
            label="Usage Duration"
            cellRenderer={visitCellRenderer}
            disableSort={true}
          />
          <Column
            key="exposureEvent"
            dataKey="exposureEvent"
            width={15}
            label="Exposure Event"
            headerRenderer={tooltipHeaderRenderer(
              '"Exposed" indicates a visit to a room occupied by a contagious patient or employee.'
            )}
            cellRenderer={exposureCellRenderer}
            disableSort={true}
          />
        </DataTable>
      </div>
      <div className="total-results">
        {props.visits && (
          <div>
            {props.visits.length}{" "}
            {props.visits.length === 1 ? "result" : "results"}
          </div>
        )}
      </div>
    </div>
  )
}

ExposedAssetVisitList.propTypes = {
  asset: PropTypes.shape({
    name: PropTypes.string,
    ...AssetSummary.propTypes,
  }).isRequired,
  entities: entitiesPropTypes,
  loading: PropTypes.bool,
  visits: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      duration: PropTypes.number,
      unit: PropTypes.string,
      name: PropTypes.string,
      entryAt: PropTypes.string,
      exitAt: PropTypes.string,
    })
  ),
}

ExposedAssetVisitList.defaultProps = {
  entities: [],
  visits: [],
}

export default ExposedAssetVisitList
